import { Fragment } from 'react';

import { NavLink as RouterLink } from 'react-router-dom';

import {
  Button,
  Checkbox,
  HStack,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';

import { CellContext, ColumnDef, createColumnHelper, Row, Table } from '@tanstack/react-table';

import { format, parseISO } from 'date-fns';

import {
  TransactionDirection,
  TransactionStatus,
  ExternalLinkOutline,
  MoreCircleOutline,
} from '@aslanpay/portal-common-lib';

import {
  SuspiciousTransactionActions,
  SuspiciousTransactionBulkActions,
  TransactionRow,
} from 'app/components/UI/Table/Table.types';
import TextWithClipboard from 'app/components/UI/TextWithClipboard';

import {
  SuspiciousTransactionColumns,
  suspiciousTransactionsActionsValues,
} from 'app/constants/table';
import { TransactionDirectionLabels, TransactionStatusLabels } from 'app/constants/transactions';
import { countries } from 'app/constants/countries';

import { getEmployeeProfilePath, getSelectedCompanyPath } from 'app/utils/routes';
import getTribeAdminLink from 'app/utils/tribe';

function getSuspiciousTransactionColumns(actions: SuspiciousTransactionActions) {
  const columnHelper = createColumnHelper<TransactionRow>();

  const fields = Object.keys(SuspiciousTransactionColumns) as Array<keyof TransactionRow>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<TransactionRow, any>[] = fields.map((field) => {
    return columnHelper.accessor(field as keyof TransactionRow, {
      cell: ({ row, getValue }) => {
        const value = getValue();

        switch (field as keyof typeof SuspiciousTransactionColumns) {
          case 'id':
          case 'externalId':
            return value && <TextWithClipboard value={value as string} />;
          case 'employeeName':
            return (
              <Link
                as={RouterLink}
                to={getEmployeeProfilePath(row.original.individualId)}
                color="brand.purple"
                textDecoration="underline"
                onClick={(event) => event.stopPropagation()}
              >
                {value as string}
              </Link>
            );
          case 'companyName':
            return (
              <Link
                as={RouterLink}
                to={getSelectedCompanyPath(row.original.companyId)}
                color="brand.purple"
                textDecoration="underline"
                onClick={(event) => event.stopPropagation()}
              >
                {value as string}
              </Link>
            );
          case 'createdAt':
            return value ? (
              <Text color="brand.purple">
                {format(parseISO(value as string), 'yyyy-MM-dd HH:mm:ss')}
              </Text>
            ) : (
              ''
            );
          case 'transactionDirection':
            return TransactionDirectionLabels[value as TransactionDirection];
          case 'transactionStatus':
            return TransactionStatusLabels[value as TransactionStatus];
          case 'countryCode':
            return countries.find((country) => country.code === value)?.details.name;
          case 'amount':
          case 'originalAmount':
          case 'fxFee':
            return (value as number)?.toFixed(2);
          case 'conversionRate':
            return (value as number)?.toFixed(6);
          case 'riskRuleCodes':
            return (value as string[])?.join(', ');
          case 'externalCardId':
            return value ? (
              <HStack>
                <Text>{value as string}</Text>
                <Link
                  href={getTribeAdminLink(value as string, 'card')}
                  target="_blank"
                  color="brand.purple"
                  display="flex"
                >
                  <ExternalLinkOutline boxSize="16px" />
                </Link>
              </HStack>
            ) : (
              value
            );
          default:
            return value;
        }
      },
      header: SuspiciousTransactionColumns[field as keyof typeof SuspiciousTransactionColumns],
      enableSorting: !['employeeName', 'companyName'].includes(field),
    });
  });

  columns.unshift({
    id: 'select',
    enableSorting: false,
    header: ({ table }: { table: Table<TransactionRow> }) => (
      <Checkbox
        {...{
          isChecked: table.getIsAllPageRowsSelected(),
          isIndeterminate: table.getIsSomePageRowsSelected(),
          onChange: table.getToggleAllPageRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }: { row: Row<TransactionRow> }) => (
      <Checkbox
        {...{
          isChecked: row.getIsSelected(),
          isIndeterminate: row.getIsSomeSelected(),
          onChange: row.getToggleSelectedHandler(),
        }}
      />
    ),
    meta: {
      customStyles: { justifyContent: 'center' },
      pin: 'left',
      withBackground: true,
    },
  });

  const buildOptionsColumn = (id: string) =>
    columnHelper.display({
      id,
      meta: {
        getCellContext: (context: CellContext<TransactionRow, unknown>) => {
          if (context.column.id === id) return { style: { padding: 0, textAlign: 'end' } };
          return undefined;
        },
        customStyles: { justifyContent: 'center' },
        pin: 'right',
      },
      cell: (info) => (
        <Menu autoSelect={false} strategy="fixed">
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<MoreCircleOutline boxSize="20px" />}
            variant="menu"
          />
          <Portal>
            <MenuList>
              {Object.keys(actions).flatMap((action, index) => {
                const onActionClick = actions[action as keyof typeof actions];

                if (onActionClick) {
                  const actionValue = suspiciousTransactionsActionsValues.find(
                    (value) => value.id === action
                  );

                  return (
                    <Fragment key={action}>
                      {index > 0 && <MenuDivider key={`${info.row.index}${action}cenas`} />}
                      <MenuItem
                        key={action}
                        color="brand.black"
                        onClick={() => onActionClick([info.row.index])}
                        icon={<Icon as={actionValue?.icon} boxSize="14px" color="brand.darkGray" />}
                      >
                        {actionValue?.value}
                      </MenuItem>
                    </Fragment>
                  );
                }
                return [];
              })}
            </MenuList>
          </Portal>
        </Menu>
      ),
    });

  columns.push(buildOptionsColumn('optionsEnd'));

  return columns;
}

export function getSuspiciousTransactionBulkActionsValues<TData extends object>(
  actions: SuspiciousTransactionBulkActions,
  selectedRows: Row<TData>[]
) {
  const bulkActionsElements = Object.keys(actions).map((bulkAction) => {
    const actionValue = suspiciousTransactionsActionsValues.find(
      (value) => value.id === bulkAction
    );

    return (
      <Button
        key={bulkAction}
        size="bulk"
        variant="bulk"
        onClick={() =>
          actions[bulkAction as keyof SuspiciousTransactionBulkActions].action(
            selectedRows.map((row) => row.index)
          )
        }
        isDisabled={actions[bulkAction as keyof SuspiciousTransactionBulkActions].isDisabled}
      >
        <Icon as={actionValue?.icon} boxSize="20px" />
        <Text>{actionValue?.value}</Text>
      </Button>
    );
  });

  return { label: 'transaction', bulkActions: bulkActionsElements };
}

export default getSuspiciousTransactionColumns;
